import React from 'react';
import { FileText, Mail, CreditCard, Gift } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const howToBookContent = {
  en: {
    title: 'How to Book',
    subtitle: 'Securing your child\'s place is simple — just 4 easy steps.',
    steps: [
      { icon: FileText, label: 'Fill out the Sign-Up form', desc: 'Complete the online registration with player and parent details.' },
      { icon: Mail, label: 'Receive your detailed quote', desc: 'We\'ll send you a personalised quote with full program information.' },
      { icon: CreditCard, label: 'Pay the deposit', desc: '50% deposit secures your spot. Free cancellation insurance included.' },
      { icon: Gift, label: 'Receive your welcome pack', desc: 'Get everything you need to prepare for an unforgettable experience.' }
    ]
  },
  es: {
    title: 'Cómo Reservar',
    subtitle: 'Reservar la plaza de tu hijo es sencillo — solo 4 pasos.',
    steps: [
      { icon: FileText, label: 'Rellena el formulario de inscripción', desc: 'Completa el registro online con los datos del jugador y los padres.' },
      { icon: Mail, label: 'Recibe tu presupuesto detallado', desc: 'Te enviaremos un presupuesto personalizado con toda la información del programa.' },
      { icon: CreditCard, label: 'Paga el depósito', desc: 'Un depósito del 50% asegura tu plaza. Seguro de cancelación gratuito incluido.' },
      { icon: Gift, label: 'Recibe tu pack de bienvenida', desc: 'Obtén todo lo que necesitas para preparar una experiencia inolvidable.' }
    ]
  },
  fr: {
    title: 'Comment Réserver',
    subtitle: 'Réserver la place de votre enfant est simple — seulement 4 étapes.',
    steps: [
      { icon: FileText, label: 'Remplissez le formulaire d\'inscription', desc: 'Complétez l\'inscription en ligne avec les détails du joueur et des parents.' },
      { icon: Mail, label: 'Recevez votre devis détaillé', desc: 'Nous vous enverrons un devis personnalisé avec toutes les informations du programme.' },
      { icon: CreditCard, label: 'Payez l\'acompte', desc: 'Un acompte de 50% réserve votre place. Assurance annulation gratuite incluse.' },
      { icon: Gift, label: 'Recevez votre pack de bienvenue', desc: 'Obtenez tout ce qu\'il faut pour préparer une expérience inoubliable.' }
    ]
  }
};

const HowToBook = () => {
  const { language } = useLanguage();
  const content = howToBookContent[language] || howToBookContent.en;

  return (
    <section className="py-16 bg-gray-50" data-testid="how-to-book-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">{content.title}</h2>
          <p className="text-gray-600 text-base max-w-xl mx-auto">{content.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {content.steps.map((step, index) => {
            const Icon = step.icon;
            return (
              <div key={index} className="relative text-center" data-testid={`how-to-book-step-${index}`}>
                {/* Connector line (hidden on first and on mobile) */}
                {index > 0 && (
                  <div className="hidden md:block absolute top-8 -left-3 w-6 border-t-2 border-dashed border-green-300" />
                )}

                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600 text-white mb-4 mx-auto relative">
                  <Icon className="h-7 w-7" />
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-gray-900 text-white text-xs font-black rounded-full flex items-center justify-center">
                    {index + 1}
                  </span>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">{step.label}</h3>
                <p className="text-sm text-gray-500">{step.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowToBook;
